import {
  Box,
  Container,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
  Paper,
  Chip,
  Grid,
} from "@mui/material";

import {
  CheckCircle as CheckIcon,
  AutoAwesome as SparkleIcon,
} from "@mui/icons-material";
import SectionHeader from "../components/SectionHeader";

interface ComparisonProps {
  bgcolor?: string;
}

const comparisonData = [
  { task: "Oven", bnb: "Light interior wipe", deep: "Full degrease" },
  { task: "Fridge", bnb: "Wipe shelves & spills", deep: "Shelves removed & washed" },
  { task: "Floors", bnb: "Vacuum & mop", deep: "Edges & buildup" },
  { task: "Bathroom", bnb: "Guest-ready", deep: "Detail scrubbing" },
  { task: "Time", bnb: "Turnover-focused", deep: "Time-intensive" },
];

export default function Comparison({ bgcolor = "grey.50" }: ComparisonProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isVerySmall = useMediaQuery("(max-width:360px)");

  return (
    // Page background is grey so the white card pops
    <Box sx={{ width: "100vw", bgcolor }}>
      <Container
        component="section"
        maxWidth="lg"
        sx={{
          maxWidth: "1000px !important",
          py: { xs: 4, md: 7 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <SectionHeader
          title="Comparison: BNB vs Deep Cleaning"
          subtitle="How depth differs in practice for BNB / Airbnb turnovers"
        />

        {isMobile ? (
          // Mobile: keep your current per-task cards (good)
          <Stack spacing={{ xs: 1.5, md: 2 }}>
            {comparisonData.map((row, index) => (
              <Box
                key={index}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "20px",
                  bgcolor: "background.default",
                  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  sx={{
                    mb: 1.5,
                    color: "#222",
                    fontSize: { xs: "1.1rem", sm: "1.15rem" },
                    textAlign: "left",
                  }}
                >
                  {row.task}
                </Typography>

                <Grid
                  container
                  spacing={2}
                  sx={{
                    flexDirection: isVerySmall ? "column" : "row",
                  }}
                >
                  {/* BNB Column */}
                  <Grid item xs={isVerySmall ? 12 : 6}>
                    <Stack spacing={0.75}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <CheckIcon sx={{ fontSize: 16, color: "#4CAF50" }} />
                        <Chip
                          label="BNB"
                          size="small"
                          sx={{
                            height: "20px",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            bgcolor: "rgba(76, 175, 80, 0.1)",
                            color: "#4CAF50",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#333",
                          fontSize: { xs: "0.875rem", sm: "0.9rem" },
                          lineHeight: 1.5,
                          textAlign: "left",
                        }}
                      >
                        {row.bnb}
                      </Typography>
                    </Stack>
                  </Grid>

                  {/* Deep Column */}
                  <Grid item xs={isVerySmall ? 12 : 6}>
                    <Stack spacing={0.75}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <SparkleIcon sx={{ fontSize: 16, color: "#03A9F4" }} />
                        <Chip
                          label="Deep"
                          size="small"
                          sx={{
                            height: "20px",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            bgcolor: "rgba(3, 169, 244, 0.1)",
                            color: "#03A9F4",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#333",
                          fontSize: { xs: "0.875rem", sm: "0.9rem" },
                          lineHeight: 1.5,
                          textAlign: "left",
                        }}
                      >
                        {row.deep}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Stack>
        ) : (
          // Desktop: Premium layout with header once + row cards (no repeated chips)
          <Paper
            sx={{
              borderRadius: "20px",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
              overflow: "hidden",
              bgcolor: "background.default",
            }}
          >
            {/* Header row (only once) */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "180px 1fr 1fr",
                gap: 5,
                px: 3,
                py: 2.5,
                bgcolor: "background.default",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography sx={{ 
                fontWeight: 800, 
                color: "#222",
                textAlign: "left",
                ml: 5,
                }}>Task</Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <CheckIcon sx={{ fontSize: 18, color: "#2E7D32" }} />
                <Chip
                  label="BNB Turnover"
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    bgcolor: "rgba(76,175,80,0.14)",
                    color: "#2E7D32",
                    "& .MuiChip-label": { px: 1.25 },
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <SparkleIcon sx={{ fontSize: 18, color: "#0277BD" }} />
                <Chip
                  label="Deep Cleaning"
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 800,
                    bgcolor: "rgba(3,169,244,0.14)",
                    color: "#0277BD",
                    "& .MuiChip-label": { px: 1.25 },
                  }}
                />
              </Stack>
            </Box>

            {/* Rows */}
            <Box sx={{
                px: 3,
                py: 2 
              }}>
              {comparisonData.map((row, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "180px 1fr 1fr",
                    gap: 5,
                    alignItems: "center",
                    py: 2.25,
                    borderRadius: "16px",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.default",
                    mb: index === comparisonData.length - 1 ? 0 : 1.5,
                    transition: "transform 120ms ease, box-shadow 120ms ease",
                    "&:hover": {
                      boxShadow: "0 12px 26px rgba(0,0,0,0.10)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {/* Task */}
                  <Typography sx={{ 
                    fontWeight: 800, 
                    color: "#222", 
                    fontSize: "0.98rem",
                    textAlign: "left",
                    ml: 5,
                    }}>
                    {row.task}
                  </Typography>

                  {/* BNB value */}
                  <Typography
                    sx={{
                      color: "#222",
                      fontSize: "0.98rem",
                      lineHeight: 1.45,
                      textAlign: "left",
                    }}
                  >
                    {row.bnb}
                  </Typography>

                  {/* Deep value */}
                  <Typography
                    sx={{
                      color: "#222",
                      fontSize: "0.98rem",
                      lineHeight: 1.45,
                      textAlign: "left",
                    }}
                  >
                    {row.deep}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
}
